import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AdminShell } from "../components/layout";
import { RouteLoadingPanel } from "../components/workspace-startup";
import {
  AdminLanding,
  RequireAuthenticated,
  RequirePermission,
  SignedOutOnly,
} from "../features/access/route-guards";
import { adminDetailRoutes, adminRouteLabel, adminSurfaceRoutes } from "./admin-routes";

const InvitationPage = lazy(() =>
  import("../features/accounts/invitation-entry-page").then((module) => ({
    default: module.MvpInvitationEntryPage,
  })),
);
const accessPages = () => import("../features/access/access-pages");
const LoginPage = lazy(() => accessPages().then((module) => ({ default: module.LoginPage })));
const PublicLegalPage = lazy(() =>
  import("../features/legal/public-legal-page").then((module) => ({
    default: module.PublicLegalPage,
  })),
);
const AccountDeletionPage = lazy(() =>
  import("../features/legal/public-legal-page").then((module) => ({
    default: module.AccountDeletionPage,
  })),
);

const surfacePages = adminSurfaceRoutes.map((route) => ({
  ...route,
  Page: lazy(route.handle.loadPage),
}));
const detailPages = adminDetailRoutes.map((route) => ({
  ...route,
  Page: lazy(route.handle.loadPage),
}));

export function AppRouter() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route element={<PublicLegalPage kind="privacy" />} path="/privacy-policy" />
        <Route element={<PublicLegalPage kind="terms" />} path="/terms-of-use" />
        <Route element={<AccountDeletionPage />} path="/account-deletion" />
        <Route element={<SignedOutOnly />}>
          <Route element={<LoginPage />} path="/login" />
        </Route>
        <Route element={<InvitationPage />} path="/invitation" />
        <Route element={<RequireAuthenticated />}>
          <Route element={<AdminShell />}>
            <Route element={<AdminLanding />} path="/" />
            {surfacePages.map(({ Page, path, handle }) => (
              <Route
                key={path}
                element={
                  <RequirePermission
                    permission={handle.permission}
                    alternativePermissions={handle.alternativePermissions}
                  />
                }
              >
                <Route
                  path={path}
                  handle={handle}
                  element={handle.persistent ? <TopLevelSurfaceRoute /> : <Page />}
                />
              </Route>
            ))}
            {detailPages.map(({ Page, path, handle }) => (
              <Route
                key={path}
                element={
                  <RequirePermission
                    permission={handle.permission}
                    alternativePermissions={handle.alternativePermissions}
                  />
                }
              >
                <Route path={path} handle={handle} element={<Page />} />
              </Route>
            ))}
          </Route>
        </Route>
        <Route element={<Navigate replace to="/" />} path="*" />
      </Routes>
    </Suspense>
  );
}

function TopLevelSurfaceRoute() {
  return null;
}

function RouteFallback() {
  const { pathname } = useLocation();
  const label = adminRouteLabel(pathname);
  return <RouteLoadingPanel label={label} />;
}
